import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Sprout, Clock, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import { useWalletUser, useApp, ConnectPrompt } from "@/App";
import { plantCrop, harvestCrop } from "@/api";

const CROP_EMOJI = { "ember-sprout": "🌶️", "pyro-berry": "🍓", "blaze-corn": "🌽", "phoenix-tree": "🌳" };

export default function FarmPage() {
  const { isConnected, user, address, refresh } = useWalletUser();
  const { config, showToast } = useApp();
  const [selectedCrop, setSelectedCrop] = useState("ember-sprout");
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!isConnected) return <ConnectPrompt />;
  // eslint-disable-next-line
  if (!user || !config) return <div className="pt-32 text-center text-slate-400">Loading farm...</div>;

  const crops = config.crops;
  const plots = user.plots || new Array(config.plot_count).fill(null);

  const plantAt = async (idx) => {
    try {
      await plantCrop(address, idx, selectedCrop);
      showToast(`${crops[selectedCrop].name} planted!`);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed to plant", "error"); }
  };
  const harvestAt = async (idx) => {
    try {
      const r = await harvestCrop(address, idx);
      showToast(`+${r.flame_gained} FLAME harvested 🔥`);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Not ready yet", "error"); }
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-4xl font-black flex items-center gap-2"><Sprout className="text-emerald-400" /> FLAME Farm</h1>
            <p className="text-slate-400 mt-1">Plant with credits, harvest FLAME. <Link to="/farm/valley" className="text-emerald-400 underline" data-testid="farm-go-valley-link">Try Flame Valley</Link></p>
          </div>
          <div className="flex items-center gap-3">
            <div className="chip" data-testid="farm-credits-chip"><Coins size={14} className="text-cyan-400" /> {user.credits} credits</div>
            <div className="chip" data-testid="farm-flame-chip"><Flame size={14} className="text-orange-500" /> {user.flame} FLAME</div>
          </div>
        </div>

        {/* Crop picker */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="farm-crop-picker">
          {Object.entries(crops).map(([id, c]) => (
            <button key={id} onClick={() => setSelectedCrop(id)} data-testid={`farm-crop-option-${id}`}
              className={`card-tile text-left transition ${selectedCrop === id ? "border-orange-500/60 card-flame" : ""}`}>
              <div className="text-3xl">{CROP_EMOJI[id]}</div>
              <div className="font-display font-bold mt-1">{c.name}</div>
              <div className="text-xs text-slate-400 mt-1">Cost: <span className="text-cyan-400 font-mono">{c.cost}</span> cr</div>
              <div className="text-xs text-slate-400">Yield: <span className="text-orange-400 font-mono">+{c.yield}</span> FLAME</div>
              <div className="text-xs text-slate-500">⏱ {c.grow_sec}s</div>
            </button>
          ))}
        </div>

        {/* Plots */}
        <div className="mt-6 grid grid-cols-3 gap-3 max-w-xl mx-auto" data-testid="farm-plots-grid">
          {plots.map((plot, idx) => {
            const now = Date.now();
            const ready = plot ? (new Date(plot.planted_at).getTime() + plot.grow_sec * 1000 <= now) : false;
            const remaining = plot ? Math.max(0, Math.ceil((new Date(plot.planted_at).getTime() + plot.grow_sec * 1000 - now) / 1000)) : 0;
            return (
              <div key={idx} data-testid={`farm-plot-slot-${idx}`} className={`plot flex items-center justify-center ${ready ? "ready" : ""}`}
                onClick={() => (plot ? (ready ? harvestAt(idx) : null) : plantAt(idx))}>
                {!plot ? (
                  <div className="text-amber-200/50 text-sm font-display">+ Plant</div>
                ) : (
                  <div className="flex flex-col items-center">
                    <motion.div animate={{ scale: ready ? [1, 1.15, 1] : 1 }} transition={{ repeat: ready ? Infinity : 0, duration: 1.2 }}
                      className="text-5xl">{CROP_EMOJI[plot.crop]}</motion.div>
                    {ready ? (
                      <button data-testid={`farm-harvest-crop-button-${idx}`} className="btn-tactile btn-gold mt-2 text-xs px-3 py-1">Harvest +{plot.yield_flame}</button>
                    ) : (
                      <div className="font-pixel text-[9px] mt-2 text-slate-300 flex items-center gap-1"><Clock size={10} />{remaining}s</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
